import { sanitizeStyleId } from '../../hooks/useBeerStyles';

export function getOpenDialogFormReset({ isEditMode, editBeer }) {
  if (isEditMode && editBeer) {
    return {
      formData: {
        name: editBeer.name || '',
        abv:
          editBeer.abv !== undefined && editBeer.abv !== null
            ? editBeer.abv.toString()
            : '',
        description: editBeer.description || '',
      },
      catalogStyleId: typeof editBeer.style === 'string' ? editBeer.style : '',
      styleLabel: typeof editBeer.style === 'string' ? editBeer.style : '',
      styleType: '',
    };
  }

  return {
    formData: { name: '', abv: '', description: '' },
    catalogStyleId: '',
    styleLabel: '',
    styleType: '',
  };
}

export function getEditStyleHydrationFields({
  editBeer,
  beerStyles,
  styleType,
  catalogStyleId,
  styleLabel,
  styleFieldsTouched,
}) {
  if (!editBeer) {
    return null;
  }

  const editStyleId = typeof editBeer.style === 'string' ? editBeer.style : '';
  const isInitialPlaceholder =
    styleType === ''
    && catalogStyleId === editStyleId
    && styleLabel === editStyleId;

  if (styleFieldsTouched && !isInitialPlaceholder) {
    return null;
  }

  const matchedStyle = beerStyles.find((styleRow) => styleRow.id === editBeer.style);
  if (!matchedStyle) {
    return null;
  }

  return {
    styleType: matchedStyle.type || '',
    catalogStyleId: editStyleId,
    styleLabel: matchedStyle.name || editBeer.style || '',
  };
}

export function validateAddBeerForm({
  formData,
  styleType,
  catalogStyleId,
  styleLabel,
  isEditMode,
  breweryLocked,
  selectedBrewery,
}) {
  const errors = {};

  if (!formData.name || !formData.name.trim()) {
    errors.name = 'Name is required';
  }
  if (!styleType || !styleType.trim()) {
    errors.styleType = 'Style type is required';
  }
  if (!catalogStyleId && (!styleLabel || !styleLabel.trim())) {
    errors.style = 'Beer style is required';
  }
  if (!formData.abv || formData.abv.trim() === '') {
    errors.abv = 'ABV is required';
  } else {
    const abvNum = parseFloat(formData.abv);
    if (Number.isNaN(abvNum)) {
      errors.abv = 'ABV must be a valid number';
    } else if (abvNum < 0) {
      errors.abv = 'ABV cannot be negative';
    } else {
      const abvStr = formData.abv.toString();
      const decimalIndex = abvStr.indexOf('.');
      if (decimalIndex !== -1 && abvStr.length - decimalIndex - 1 > 1) {
        errors.abv = 'ABV must have maximum 1 decimal place';
      }
    }
  }
  if (!isEditMode && !breweryLocked && !selectedBrewery?.id) {
    errors.brewery = 'Brewery is required';
  }

  return errors;
}

export function resolveStyleForSave({ catalogStyleId, styleLabel, styleType }) {
  if (catalogStyleId) {
    return { ok: true, value: { styleId: catalogStyleId } };
  }

  const trimmedLabel = typeof styleLabel === 'string' ? styleLabel.trim() : '';
  if (!trimmedLabel) {
    return { ok: false, errors: { style: 'Beer style is required' } };
  }

  const trimmedType = typeof styleType === 'string' ? styleType.trim() : '';
  if (!trimmedType) {
    return { ok: false, errors: { styleType: 'Style type is required' } };
  }

  const styleId = sanitizeStyleId(trimmedLabel);
  if (!styleId) {
    return { ok: false, errors: { style: 'Beer style is required' } };
  }

  return {
    ok: true,
    value: {
      styleId,
      styleName: trimmedLabel,
      styleType: trimmedType,
    },
  };
}

export function parseAbvFromForm(abvValue) {
  return Math.round(parseFloat(abvValue) * 10) / 10;
}

export function buildSavePayload({ formData, resolvedStyle, abvNum }) {
  const payload = {
    name: formData.name.trim(),
    style: resolvedStyle.styleId,
    abv: abvNum,
    description: formData.description.trim() ? formData.description.trim() : null,
  };

  if (resolvedStyle.styleName && resolvedStyle.styleType) {
    payload.styleName = resolvedStyle.styleName;
    payload.styleType = resolvedStyle.styleType;
  }

  return payload;
}

export function getDuplicateReviewId(duplicateNormalized) {
  const topLevelId =
    typeof duplicateNormalized?.beerDuplicateReviewId === 'string'
      ? duplicateNormalized.beerDuplicateReviewId.trim()
      : '';
  if (topLevelId) {
    return topLevelId;
  }

  const fromList = duplicateNormalized?.duplicatedBeers
    ?.map((entry) => entry.beerDuplicateReviewId)
    .find((reviewId) => reviewId && String(reviewId).trim());

  return fromList ? String(fromList).trim() : '';
}

export function canForceAddDuplicate(duplicateNormalized) {
  return getDuplicateReviewId(duplicateNormalized).length > 0;
}

export const FORCE_ADD_DUPLICATE_FAILURE_MESSAGE =
  'We could not add this beer because it still matches an existing one. Try using the existing beer or editing the name.';

export function applyNormalizedAddResponse({
  normalized,
  breweryIdValue,
  formNameTrim,
  wasForceAdd = false,
  raw = null,
  onSuccess,
  handleClose,
  setDuplicateNormalized,
  setServerFieldErrors,
  setFormErrors,
}) {
  if (!normalized || typeof normalized !== 'object') {
    setDuplicateNormalized(null);
    setServerFieldErrors(null);
    setFormErrors({ general: 'Unexpected response from server.' });
    return;
  }

  if (normalized.errorCount > 0) {
    setDuplicateNormalized(null);
    setServerFieldErrors(normalized.errors);
    return;
  }

  if (normalized.duplicatedCount > 0) {
    setDuplicateNormalized(normalized);
    if (wasForceAdd) {
      if (import.meta.env.DEV) {
        console.warn(
          '[AddBeerDialog] Force add still returned duplicates; inspect [useBeerAddBeer] log for raw payload',
          { normalized, raw },
        );
      }
      setFormErrors({
        general: FORCE_ADD_DUPLICATE_FAILURE_MESSAGE,
      });
    }
    return;
  }

  if (normalized.updatedCount > 0) {
    const updatedBeer = normalized.updatedBeers[0]?.updatedBeer ?? {};
    if (onSuccess) {
      onSuccess({
        outcome: 'updated',
        beerId: updatedBeer.id ?? '',
        beerName: updatedBeer.name ?? '',
        breweryId: breweryIdValue,
      });
    }
    handleClose();
    return;
  }

  if (normalized.addedCount > 0) {
    const addedBeer = normalized.addedBeers[0] ?? {};
    if (onSuccess) {
      onSuccess({
        outcome: 'added',
        beerId: addedBeer.id ?? '',
        beerName: addedBeer.name || formNameTrim,
        breweryId: breweryIdValue,
      });
    }
    handleClose();
    return;
  }

  if (
    wasForceAdd
    && normalized.errorCount === 0
    && normalized.duplicatedCount === 0
    && normalized.updatedCount === 0
    && normalized.addedCount === 0
  ) {
    if (import.meta.env.DEV) {
      console.warn('[AddBeerDialog] Force add: empty normalized outcome', { normalized, raw });
    }
    setDuplicateNormalized(null);
    setFormErrors({
      general:
        'The server did not report an added or updated beer. Open the devtools console for the logged raw response.',
    });
    return;
  }

  setDuplicateNormalized(null);
  setFormErrors({ general: 'Unexpected response from server.' });
}

export function getDialogTitle({ duplicateNormalized, isEditMode }) {
  if (duplicateNormalized && !isEditMode) {
    return 'Possible duplicates';
  }
  if (isEditMode) {
    return 'Edit beer';
  }
  return 'Add beer';
}

export function isGroupDuplicateOffer(duplicateNormalized) {
  const duplicatedBeers = duplicateNormalized?.duplicatedBeers;
  if (!Array.isArray(duplicatedBeers) || duplicatedBeers.length === 0) {
    return false;
  }
  return duplicatedBeers.some(
    (entry) => entry?.rationale === 'exact group name match',
  );
}

export function getInitialLocationIds(editBeer) {
  if (!editBeer || typeof editBeer !== 'object') {
    return [];
  }

  const primaryId =
    typeof editBeer.breweryId === 'string' ? editBeer.breweryId.trim() : '';
  if (Array.isArray(editBeer.availableBreweryIds) && editBeer.availableBreweryIds.length > 0) {
    return editBeer.availableBreweryIds.filter(
      (breweryId) => typeof breweryId === 'string' && breweryId.trim().length > 0,
    );
  }

  return primaryId ? [primaryId] : [];
}

export function haveLocationIdsChanged(initialIds, selectedIds) {
  const normalizeIds = (ids) => Array.from(
    new Set(
      (Array.isArray(ids) ? ids : [])
        .filter((breweryId) => typeof breweryId === 'string' && breweryId.trim().length > 0)
        .map((breweryId) => breweryId.trim()),
    ),
  ).sort();

  const normalizedInitial = normalizeIds(initialIds);
  const normalizedSelected = normalizeIds(selectedIds);

  if (normalizedInitial.length !== normalizedSelected.length) {
    return true;
  }

  return normalizedInitial.some(
    (breweryId, index) => breweryId !== normalizedSelected[index],
  );
}

export function getMatchedBeerForReuse(duplicateNormalized) {
  const duplicatedBeers = duplicateNormalized?.duplicatedBeers;
  if (!Array.isArray(duplicatedBeers) || duplicatedBeers.length === 0) {
    return null;
  }
  const groupMatch = duplicatedBeers.find(
    (entry) => entry?.rationale === 'exact group name match' && entry?.matchedBeer?.id,
  );
  if (groupMatch?.matchedBeer) {
    return groupMatch.matchedBeer;
  }
  const firstMatch = duplicatedBeers.find((entry) => entry?.matchedBeer?.id);
  return firstMatch?.matchedBeer ?? null;
}

/** Default wait for Firestore catalog listener after a failed callable (ms). */
export const PORTFOLIO_RECONCILE_DELAY_MS = 2000;

/**
 * Case-insensitive name match for a beer in the portfolio catalog.
 * @param {object} params
 * @param {Array<{ id?: string, name?: string, breweryId?: string, availableBreweryIds?: string[] }>} params.catalogBeers
 * @param {string} params.beerName
 * @param {string} params.breweryId
 * @param {Set<string>} [params.excludeBeerIds] - IDs present before submit (ignore pre-existing matches)
 * @returns {{ id: string, name: string, breweryId?: string } | null}
 */
export function findBeerInCatalog({
  catalogBeers,
  beerName,
  breweryId,
  excludeBeerIds = null,
}) {
  const nameTrim = typeof beerName === 'string' ? beerName.trim() : '';
  const breweryIdTrim = typeof breweryId === 'string' ? breweryId.trim() : '';
  if (!nameTrim || !breweryIdTrim || !Array.isArray(catalogBeers)) {
    return null;
  }

  const normalizedName = nameTrim.toLowerCase();
  const match = catalogBeers.find((beerRow) => {
    if (!beerRow || typeof beerRow.name !== 'string') {
      return false;
    }
    if (beerRow.name.trim().toLowerCase() !== normalizedName) {
      return false;
    }
    if (excludeBeerIds instanceof Set && excludeBeerIds.has(beerRow.id)) {
      return false;
    }
    const primaryId =
      typeof beerRow.breweryId === 'string' ? beerRow.breweryId.trim() : '';
    if (primaryId === breweryIdTrim) {
      return true;
    }
    if (Array.isArray(beerRow.availableBreweryIds)) {
      return beerRow.availableBreweryIds.some(
        (locationId) => typeof locationId === 'string' && locationId.trim() === breweryIdTrim,
      );
    }
    return false;
  });

  if (!match?.id) {
    return null;
  }

  return {
    id: match.id,
    name: match.name || nameTrim,
    breweryId: match.breweryId || breweryIdTrim,
  };
}

/**
 * Waits briefly so the realtime catalog listener can catch up after a failed callable.
 * @param {number} [delayMs]
 * @returns {Promise<void>}
 */
export function waitForPortfolioReconcile(delayMs = PORTFOLIO_RECONCILE_DELAY_MS) {
  return new Promise((resolve) => {
    globalThis.setTimeout(resolve, delayMs);
  });
}

/**
 * After a failed addBeer callable, check whether the beer already appears in the catalog.
 * @param {object} params
 * @param {Array<object>|(() => Array<object>)} params.catalogBeers - snapshot or getter (getter preferred after delay)
 * @param {string} params.beerName
 * @param {string} params.breweryId
 * @param {Set<string>} [params.knownCatalogBeerIds] - beer IDs in catalog before submit
 * @param {number} [params.delayMs]
 * @returns {Promise<{ id: string, name: string, breweryId?: string } | null>}
 */
export async function reconcileAddBeerAfterCallableError({
  catalogBeers,
  beerName,
  breweryId,
  knownCatalogBeerIds = null,
  delayMs = PORTFOLIO_RECONCILE_DELAY_MS,
}) {
  const readCatalog = () => (
    typeof catalogBeers === 'function' ? catalogBeers() : catalogBeers
  );
  const excludeBeerIds = knownCatalogBeerIds instanceof Set ? knownCatalogBeerIds : null;

  const immediate = findBeerInCatalog({
    catalogBeers: readCatalog(),
    beerName,
    breweryId,
    excludeBeerIds,
  });
  if (immediate) {
    return immediate;
  }
  await waitForPortfolioReconcile(delayMs);
  return findBeerInCatalog({
    catalogBeers: readCatalog(),
    beerName,
    breweryId,
    excludeBeerIds,
  });
}
